import Modal from 'components/Modal/Modal'
import {Formik} from 'formik'
import React, {useContext, useMemo} from 'react'
import useTranslate from 'modules/local/useTranslate'
import {NavigationContext} from 'modules/navigation/NavigationContext'
import {post_create_Api} from 'apis'
import MegaTextarea from 'components/Feed/InputBoard/MegaTextarea'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {NullAsDefault} from 'views/Shared'
import MentionDataContext from 'components/Feed/MentionDataContext'
import {getId, getType} from 'apis/model/base'
import Pure from 'components/Pure'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {LoginContext} from 'components/LoginContext'
import {getContentSchema} from 'apis/schema'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import _ from 'lodash'
import {AnnouncementFeedCard, EmbedFeedCard, UserCheckinFeedCard, WoGFeedCard} from 'components/Feed/TimeLineFeed'
import {createValue} from 'components/form/utils'
import LookupField from 'components/LookupField'
import PostSettingForm from 'components/Feed/PostForm/PostSettingForm'
import PostFormContext from 'components/Feed/PostForm/PostFormContext'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import classNames from "classnames";

// ─── PostTypeTabs — đồng bộ design system ────────────────────────────────────

const PostTypeTabs = ({value, onChange, disabledValues = []}) => {
    const t = useTranslate()
    const tabs = [
        {value: 'post',   labelKey: 'news post'},
        {value: 'prayer', labelKey: 'prayer intention'},
    ]
    return (
        <div
            style={{ padding: 5, borderRadius: 10 }}
            className="w-fit flex items-center gap-1 bg-slate-100">
            {tabs.map((tab) => {
                const active = value === tab.value
                const disabled = disabledValues.includes(tab.value)
                return (
                    <button
                        key={tab.value}
                        type="button"
                        onClick={() => !disabled && onChange(tab.value)}
                        disabled={disabled}
                        style={{
                            padding: '0.25rem 0.75rem', borderRadius: '0.375rem',
                            fontSize: 14, fontWeight: active ? 600 : 500,
                            color: active ? 'var(--color-secondary-600)' : disabled ? '#cbd5e1' : '#64748b',
                        }}
                        className={classNames(
                            'tracking-wide whitespace-no-wrap transition-all duration-300',
                            active ? 'background' : disabled ? 'bg-transparent cursor-not-allowed' : 'hover:bg-slate-200'
                        )}>
                        {t(tab.labelKey)}
                    </button>
                )
            })}
        </div>
    )
}

// ─── FeedQuoteForm ────────────────────────────────────────────────────────────

const FeedQuoteForm = ({type, id, onCancel}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const {isSm, isLg} = useContext(LayoutContext)

    // useSelectEntities — giữ nguyên, đây là data fetching logic
    const item = useSelectEntities(id, getContentSchema(type))

    // mentions giữ nguyên từ item — read-only từ server
    const mentions = Array.from(item?.mentions ?? [])

    const {push = NullAsDefault, toString = NullAsDefault} = useContext(MentionDataContext)
    const {options} = useContext(PostFormContext)

    const {handleAsyncAction, isLoading: isSubmitting} = useAsyncAction({
        onError: notifyOnError(t),
        apiInfo: post_create_Api,
        onSuccess: () => {
            successNotify(NotificationActionTypes.CREATE)
            onCancel()
        },
    })

    // defaultChannel logic giữ nguyên — tìm channel của login user
    const defaultChannel = useMemo(() => {
        return _.first(
            (options || []).filter(
                (e) =>
                    getType(_.get(e, 'data.owner')) === getType(login) &&
                    getId(_.get(e, 'data.owner')) === getId(login)
            )
        )
    }, [options, login])

    const _initialValues = useMemo(() => ({
        embed_type:           getType(item),
        embed_id:             getId(item),
        description:          '',
        privacy:              1,
        post_type:            'post',
        containers:           options,
        default_owner_id:     login?.id,
        default_container_id: defaultChannel?.value,
    }), [item, options, login, defaultChannel])

    // renderFeedCard giữ nguyên — switch case không thay đổi
    const renderFeedCard = useMemo(() => {
        const type = getType(item)
        switch (type) {
            case 'god_word':                    return <WoGFeedCard item={item}/>
            case 'user_checkin':                return <UserCheckinFeedCard item={item}/>
            case 'announcement':                return <AnnouncementFeedCard item={item}/>
            case 'prayer':
            default:                            return <EmbedFeedCard item={item}/>
        }
    }, [item])

    // onSubmit logic giữ nguyên hoàn toàn — không thay đổi gì
    const handleSubmitValues = (values, handleAsyncAction) => {
        const {
            owner, container, containers,
            default_owner_id, default_container_id,
            isLoading,
            ...rest
        } = values ?? {}
        handleAsyncAction(
            _.omitBy(
                {
                    owner_id:       getId(owner?.data) ? getId(owner?.data) : getId(login),
                    owner_type:     getId(owner?.data) ? getType(owner?.data) : 'user',
                    container_id:   getId(container?.data) ? getId(container?.data) : default_container_id,
                    container_type: getId(container?.data) ? getType(container?.data) : 'channel',
                    ...rest,
                },
                _.isUndefined
            ),
            {
                id:   getId(container?.data) ? getId(container?.data) : default_container_id,
                prop: getId(container?.data) ? getType(container?.data) : 'channel',
            }
        )
    }

    const renderForm = (cancel) => item && (
        <Formik
            validate={() => ({})}
            enableReinitialize={true}
            onSubmit={(values) => handleSubmitValues(values, handleAsyncAction)}
            initialValues={_initialValues}>
            {({values, handleSubmit, handleChange}) => {
                const isPrayer = values?.post_type === 'prayer'
                return (
                    <React.Fragment>

                        {/* ── Header — mobile: sticky top, đè lên nội dung khi cuộn ── */}
                        {!isLg && (
                            <div
                                className="flex items-center justify-between background sticky z-20 top-0 p-3"
                                style={{
                                    borderBottom: '1px solid var(--border-color-50, #f0f0f0)',
                                }}>
                                <button
                                    type="button"
                                    onClick={cancel}
                                    style={{
                                        fontSize: 13,
                                        color: 'var(--color-text-400, #888)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        minWidth: 48,
                                        textAlign: 'left',
                                        padding: '4px 0',
                                    }}>
                                    {t('close')}
                                </button>

                                <span style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: 'var(--color-text-000)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                }}>
                                    {t('repost')}
                                </span>

                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={!!isSubmitting}
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        background: !isSubmitting ? 'var(--color-primary)' : 'var(--background-200, #e0e0e0)',
                                        color: !isSubmitting ? '#fff' : 'var(--color-text-400, #aaa)',
                                        border: 'none',
                                        borderRadius: 20,
                                        padding: '7px 18px',
                                        cursor: !isSubmitting ? 'pointer' : 'not-allowed',
                                        transition: 'all .15s',
                                        minWidth: 72,
                                        whiteSpace: 'nowrap',
                                    }}>
                                    {isSubmitting ? '...' : t('publish a post')}
                                </button>
                            </div>
                        )}

                        <div className={classNames('flex flex-col gap-4', !isLg && 'p-3')}>

                        {/* ── Channel row ── */}
                        <PostSettingForm/>

                        {/* ── Tabs — Ý nguyện disabled khi đăng lại ── */}
                        <PostTypeTabs
                            value={values?.post_type}
                            onChange={(val) => handleChange(createValue('post_type', val))}
                            disabledValues={['prayer']}
                        />

                        {/* ── Prayer category ── */}
                        {isPrayer && (
                            <LookupField
                                createIfNotExists={true}
                                defaultValue={values?.prayer_category}
                                apiPath="/lookup/prayer_category/gets"
                                placeholder={t('choose prayer type')}
                                name="prayer_category"
                                renderOption={(o, i) => (
                                    <div key={i} className="flex flex-col justify-center flex-1">
                                        <span className="font-semibold text-sm text-color-000 tracking-wide leading-snug">
                                            {o?.label}
                                        </span>
                                        {o?.description && (
                                            <span className="text-xs text-color-300 italic tracking-wider leading-snug">
                                                {o?.description}
                                            </span>
                                        )}
                                    </div>
                                )}
                                handleChange={(e) => handleChange(createValue('prayer_category', e.target.value))}
                            />
                        )}

                        {/* ── Textarea + Embed — gộp 1 khung duy nhất ── */}
                        <div style={{
                            border: '1px solid var(--border-color-50, #efefef)',
                            borderRadius: 12,
                            overflow: 'hidden',
                        }}>
                            {/* Textarea — không border riêng */}
                            <MegaTextarea
                                rows={isSm ? 5 : 3}
                                values={{}}
                                name="description"
                                value={values?.description}
                                onChange={handleChange}
                                placeholder={
                                    isPrayer
                                        ? t('share your prayer intention')
                                        : t('share your thoughts')
                                }
                                wrapperClassName="flex flex-col gap-3 p-3"
                                mention_count={mentions.length}
                                fieldNames={{
                                    embed_data: 'embed_data',
                                    description: 'description',
                                }}
                                onMentioned={(mention) => push(mention)}
                                renderActions={() => null}
                            />

                            {/* Divider */}
                            <div style={{height: 1, background: 'var(--border-color-50, #efefef)'}}/>

                            {/* Embed preview — chỉ chừa khoảng thở nhỏ, khung/nền phân biệt
                                embed đã có sẵn trên chính component (FeedCardContentEmbed).
                                Ẩn nút "..." (pin/edit/xoá/report bài gốc) — không hợp lệ khi
                                đang xem preview lúc soạn bài, xem class RepostPreviewEmbed trong custom.css */}
                            <div className="py-2 RepostPreviewEmbed">
                                <Pure input={[item]}>
                                    {renderFeedCard}
                                </Pure>
                            </div>
                        </div>

                        {/* ── Footer — desktop only, mobile đã có nút Đăng trên header ── */}
                        {isLg && (
                            <div
                                className="flex items-center justify-end gap-3"
                                style={{
                                    paddingTop: 8,
                                    borderTop: '1px solid var(--border-color-50, #f0f0f0)',
                                }}>
                                <button
                                    type="button"
                                    onClick={cancel}
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: 'var(--color-text-500, #555)',
                                        background: 'none',
                                        border: '1px solid var(--border-color-50, #f0f0f0)',
                                        borderRadius: 8,
                                        padding: '7px 18px',
                                        cursor: 'pointer',
                                    }}>
                                    {t('close')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={!!isSubmitting}
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        background: !isSubmitting ? 'var(--color-primary)' : 'var(--background-200, #e8e8e8)',
                                        color: !isSubmitting ? '#fff' : 'var(--color-text-400, #aaa)',
                                        border: 'none',
                                        borderRadius: 8,
                                        padding: '7px 22px',
                                        cursor: !isSubmitting ? 'pointer' : 'not-allowed',
                                        transition: 'background .15s',
                                    }}>
                                    {isSubmitting ? '...' : t('publish a post')}
                                </button>
                            </div>
                        )}
                        </div>
                    </React.Fragment>
                )
            }}
        </Formik>
    )

    return (
        <NavigationContext.Consumer>
            {({handleGoBack}) => {
                const cancel = onCancel || handleGoBack
                return !isLg ? (
                    <FullSideModalLayout renderHeader={() => null} onCancel={cancel}>
                        {renderForm(cancel)}
                    </FullSideModalLayout>
                ) : (
                    <Modal
                        width={540}
                        className="custom-modal"
                        open={true}
                        title={
                            <div className="text-center text-color-000 font-bold uppercase">
                                {t('repost')}
                            </div>
                        }
                        footer={null}
                        onCancel={cancel}>
                        {renderForm(cancel)}
                    </Modal>
                )
            }}
        </NavigationContext.Consumer>
    )
}

export default FeedQuoteForm